import type * as THREE from 'three'
import type { GSplatLodRefinementMode } from '@/shared/types/gsplat'

/**
 * GSplat 全局参数配置
 *
 * 核心功能：
 * 1. LOD 距离和范围控制
 * 2. Splat 预算和 Underfill 策略
 * 3. 冷却机制和更新阈值
 * 4. SH 颜色更新控制
 * 5. 调试和可视化选项
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatParams 类
 * 参数修改会自动设置 dirty 标记触发更新
 */
export class GSplatParams {
  /**
   * Global splat budget for octree LOD selection.
   * Mirrors PlayCanvas scene.gsplat.splatBudget.
   * @default 0
   */
  maxCachePoints: number = 0

  // ==================== LOD 控制参数 ====================

  /**
   * LOD 更新距离阈值（世界单位）
   * 相机移动超过此距离时触发 LOD 重新评估
   * @default 1
   */
  lodUpdateDistance: number = 1

  /**
   * LOD 更新角度阈值（度数）
   * 相机旋转超过此角度时触发 LOD 重新评估
   * 设为 0 禁用基于旋转的更新
   * @default 0
   */
  lodUpdateAngle: number = 0

  // ==================== LOD 控制参数（带 dirty 标记）====================

  /**
   * @private
   */
  private _lodBehindPenalty: number = 1

  /**
   * 背后惩罚因子
   * 应用于相机后方节点的距离乘数，用于降低其 LOD
   * - 1 = 无惩罚
   * - >1 = 后方节点使用更粗糙的 LOD
   * @default 1
   */
  get lodBehindPenalty(): number {
    return this._lodBehindPenalty
  }
  set lodBehindPenalty(value: number) {
    if (this._lodBehindPenalty !== value) {
      this._lodBehindPenalty = value
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _lodRangeMin: number = 0

  /**
   * 最小允许 LOD 索引（包含）
   * @default 0
   */
  get lodRangeMin(): number {
    return this._lodRangeMin
  }
  set lodRangeMin(value: number) {
    if (this._lodRangeMin !== value) {
      this._lodRangeMin = value
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _lodRangeMax: number = 10

  /**
   * 最大允许 LOD 索引（包含）
   * @default 10
   */
  get lodRangeMax(): number {
    return this._lodRangeMax
  }
  set lodRangeMax(value: number) {
    if (this._lodRangeMax !== value) {
      this._lodRangeMax = value
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _lodUnderfillLimit: number = 0

  /**
   * Underfill 限制
   * 当最优 LOD 未加载时，允许使用比最优 LOD 粗糙的最大级数
   * - 0 = 禁用回退，始终加载最优 LOD
   * - >0 = 允许临时使用粗糙数据加速加载
   * @default 0
   */
  get lodUnderfillLimit(): number {
    return this._lodUnderfillLimit
  }
  set lodUnderfillLimit(value: number) {
    if (this._lodUnderfillLimit !== value) {
      this._lodUnderfillLimit = value
      this.dirty = true
    }
  }

  /**
   * Underfill 粗 LOD 可见后的细化策略
   * - step = 按相邻有效 LOD 逐级细化
   * - direct = 保持当前粗 LOD，直接准备当前最优有效 LOD
   * @default 'step'
   */
  lodRefinementMode: GSplatLodRefinementMode = 'step'

  // ==================== 资源管理参数 ====================

  /**
   * 冷却帧数
   * 资源引用计数归零后，保留在内存中的帧数
   * - 0 = 立即卸载
   * - >0 = 延迟卸载，避免频繁加载/卸载
   * @default 100
   */
  cooldownTicks: number = 100

  /**
   * 每帧最大加载节点数量
   * 限制每帧触发的新文件加载数量，避免单帧加载过多节点导致帧率下降
   * - 0 = 无限制
   * - >0 = 限制为指定数量
   * @default 0
   */
  maxLoadsPerFrame: number = 0

  /**
   * WorldState 更新间隔（帧数）
   * 批量累积 Placement 变化，减少 WorldState 创建频率
   * - 0 = 每帧更新（无节流）
   * - >0 = 每 N 帧更新一次
   * @default 0
   */
  worldStateUpdateInterval: number = 0

  // ==================== 排序参数 ====================

  /**
   * 启用径向排序模式（适用于球形场景/VR）
   * - false = 方向排序（沿相机前向方向）
   * - true = 径向排序（基于到相机的距离）
   *
   * 注意：径向排序可减少相机旋转时的排序伪影，
   * 而方向排序更适合相机平移时减少伪影
   * @default true
   */
  radialSorting: boolean = true

  /**
   * @private
   */
  private _sortVisibilityCompact: boolean = false

  /**
   * 启用排序 Worker 侧的可见集 compact。
   *
   * 第一阶段只做保守的相机后方中心点剔除，用于 A/B 验证
   * forward `DrawIndexedInstanced(768, X)` 中 X 的动态下降收益。
   *
   * @default false
   */
  get sortVisibilityCompact(): boolean {
    return this._sortVisibilityCompact
  }
  set sortVisibilityCompact(value: boolean) {
    if (this._sortVisibilityCompact !== value) {
      this._sortVisibilityCompact = value
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _sortVisibilityScreenCompact: boolean = false

  /**
   * 启用排序 Worker 侧的 NDC 屏幕外 compact。
   *
   * 默认关闭；显式开启后该策略依赖 sorter 提供的半径 / opacity / covariance bounds，
   * worker 会用屏幕空间 projected radius 扩张 NDC 边界，缺少 bounds 时不做屏幕剔除。
   *
   * @default false
   */
  get sortVisibilityScreenCompact(): boolean {
    return this._sortVisibilityScreenCompact
  }
  set sortVisibilityScreenCompact(value: boolean) {
    if (this._sortVisibilityScreenCompact !== value) {
      this._sortVisibilityScreenCompact = value
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _sortVisibilityScreenMargin: number = 1

  /**
   * 屏幕外中心点 compact 的 NDC 保守余量。
   *
   * 1 表示 center 需要超过屏幕外一整个 NDC 宽度 / 高度才会被跳过；
   * 值越小剔除越激进，值越大越保守。
   *
   * @default 1
   */
  get sortVisibilityScreenMargin(): number {
    return this._sortVisibilityScreenMargin
  }
  set sortVisibilityScreenMargin(value: number) {
    const nextValue = Number.isFinite(value) ? Math.max(0, value) : 1
    if (this._sortVisibilityScreenMargin !== nextValue) {
      this._sortVisibilityScreenMargin = nextValue
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _sortVisibilityBehindMargin: number = 0.75

  /**
   * 相机后方剔除的保守余量（世界单位）。
   *
   * center 到相机前向半空间的投影大于该值时，sort compact 才会跳过该 splat。
   * 值越大越保守，0 表示与顶点 shader 的相机后方中心点早退最接近。
   *
   * @default 0.75
   */
  get sortVisibilityBehindMargin(): number {
    return this._sortVisibilityBehindMargin
  }
  set sortVisibilityBehindMargin(value: number) {
    const nextValue = Number.isFinite(value) ? Math.max(0, value) : 0.75
    if (this._sortVisibilityBehindMargin !== nextValue) {
      this._sortVisibilityBehindMargin = nextValue
      this.dirty = true
    }
  }

  /**
   * @private
   */
  private _sortVisibilityRotationRefreshAngle: number = 12

  /**
   * Radial sort compact refresh angle in degrees.
   *
   * Radial order itself is position-dependent; this only controls how often
   * visibility compact refreshes while the camera rotates in place.
   *
   * @default 12
   */
  get sortVisibilityRotationRefreshAngle(): number {
    return this._sortVisibilityRotationRefreshAngle
  }
  set sortVisibilityRotationRefreshAngle(value: number) {
    const nextValue = Number.isFinite(value) ? Math.max(0, value) : 12
    if (this._sortVisibilityRotationRefreshAngle !== nextValue) {
      this._sortVisibilityRotationRefreshAngle = nextValue
      this.dirty = true
    }
  }

  // ==================== SH 颜色更新参数 ====================

  /**
   * 启用调试着色，可视化球谐函数何时被评估
   * 当为 true 时，每次更新通道使用随机颜色渲染，
   * 用于可视化 colorUpdateDistance 和 colorUpdateAngle 阈值的行为
   * @default false
   */
  colorizeColorUpdate: boolean = false

  /**
   * 触发球谐函数颜色更新的距离阈值（世界单位）
   * 用于控制基于相机平移的 SH 评估频率
   * 仅影响包含球谐数据的资源
   * 设为 0 时每次相机移动都会更新
   * @default 0.2
   */
  colorUpdateDistance: number = 0.2

  /**
   * 触发球谐函数颜色更新的角度阈值（度数）
   * 用于控制基于相机旋转的 SH 评估频率
   * 仅影响包含球谐数据的资源
   * 设为 0 时每次相机旋转都会更新
   * @default 2
   */
  colorUpdateAngle: number = 2

  /**
   * 每个 LOD 级别的 colorUpdateDistance 缩放因子
   * 每个 LOD 级别将阈值乘以此值的 lodIndex 次幂
   * 例如，scale=2：LOD 0 使用 1x 阈值，LOD 1 使用 2x，LOD 2 使用 4x
   * 较高的值会更激进地放宽远处几何体的阈值
   * @default 2
   */
  colorUpdateDistanceLodScale: number = 2

  /**
   * 每个 LOD 级别的 colorUpdateAngle 缩放因子
   * 每个 LOD 级别将阈值乘以此值的 lodIndex 次幂
   * 例如，scale=2：LOD 0 使用 1x 阈值，LOD 1 使用 2x，LOD 2 使用 4x
   * 较高的值会更激进地放宽远处几何体的阈值
   * @default 2
   */
  colorUpdateAngleLodScale: number = 2

  /**
   * 预留的高质量 SH 开关。
   * unified LOD 当前固定使用逐 placement WorkBuffer SH 求值，避免多实例共享方向状态。
   * @default false
   */
  highQualitySH: boolean = false

  // ==================== 调试参数 ====================

  /**
   * 启用 AABB 调试渲染
   * @default false
   */
  debugAabbs: boolean = false

  /**
   * 启用八叉树节点 AABB 调试渲染
   * @default false
   */
  debugNodeAabbs: boolean = false

  /**
   * @private
   */
  private _colorizeLod: boolean = false

  /**
   * 启用 LOD 级别着色（用于可视化 LOD）
   * 修改时会触发 dirty 标记
   * @default false
   */
  get colorizeLod(): boolean {
    return this._colorizeLod
  }
  set colorizeLod(value: boolean) {
    if (this._colorizeLod !== value) {
      this._colorizeLod = value
      this.dirty = true
    }
  }

  // ==================== 过绘可视化参数 ====================

  /**
   * @private
   */
  private _colorRamp: THREE.Texture | null = null

  /**
   * 用于过绘可视化模式的渐变纹理
   * 设置后启用加法混合的过绘模式；为 null 时使用正常渲染
   * 纹理应为 (width x 1) 大小
   * 世界 Y 坐标（0-20 范围）映射到纹理 U 坐标
   * @default null
   */
  get colorRamp(): THREE.Texture | null {
    return this._colorRamp
  }
  set colorRamp(value: THREE.Texture | null) {
    if (this._colorRamp !== value) {
      this._colorRamp = value
      this.dirty = true
    }
  }

  /**
   * 过绘可视化模式的强度乘数
   * 值为 1 时使用 alpha = 1/32，允许约 32 次过绘达到加法混合的全亮度
   * 较高的值增加每个 splat 的亮度
   * @default 1
   */
  colorRampIntensity: number = 1

  // ==================== 内部标志 ====================

  /**
   * 脏标记，指示参数已更改需要更新
   * @internal
   */
  dirty: boolean = false

  /**
   * 帧结束时清理脏标记
   * @internal
   */
  frameEnd(): void {
    this.dirty = false
  }
}
